import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  Container,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  CardActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Ronda from './components/Ronda';
import Baciga from './components/Baciga';
import GameSetup from './components/GameSetup';
import Instructions from './components/Instructions';
import Header from './components/Header';
import ImageCarousel from './components/ImageCarousel';
import VersionCheckManager from './components/VersionCheckManager';
import SavedGameModal from './components/SavedGameModal';
import ChangelogModal from './components/ChangelogModal';
import TutorialModal from './components/TutorialModal';
import InlineWebAd from './components/InlineWebAd';
import gameStateManager from './services/GameStateManager';
import adMobService from './services/AdMobService';
import changelogService from './services/ChangelogService';
import tutorialService from './services/TutorialService';
import soundVibrationService from './services/SoundVibrationService';
import { shareImageService } from './services/ShareImageService';
import { usePageTracking } from './hooks/usePageTracking';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from './utils/debugUtils';

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [savedGameModalOpen, setSavedGameModalOpen] = useState(false);
  const [savedGameInfo, setSavedGameInfo] = useState(null);
  const [changelogModalOpen, setChangelogModalOpen] = useState(false);
  const [shouldShowChangelog, setShouldShowChangelog] = useState(false);
  const [tutorialModalOpen, setTutorialModalOpen] = useState(false);
  const [shouldShowTutorial, setShouldShowTutorial] = useState(false);
  const [isTestingShare, setIsTestingShare] = useState(false);
  const currentVersion = process.env.REACT_APP_VERSION;
  const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';
  const isWebPlatform = Capacitor.getPlatform() === 'web';

  // Test share functionality with mock data
  const handleTestShare = async () => {
    setIsTestingShare(true);
    try {
      const mockPlayers = [
        { name: 'Carlos', avatar: '😎' },
        { name: 'María', avatar: '👩' },
        { name: 'Pedro', avatar: '🧔' },
        { name: 'Ana', avatar: '👧' }
      ];
      await shareImageService.shareWinnerImage({
        winnerTeamId: 1,
        players: mockPlayers,
        gamePoints: 2,
        t
      });
    } catch (error) {
      debugError('Error testing share:', error);
    } finally {
      setIsTestingShare(false);
    }
  };

  // Check for saved game on mount
  useEffect(() => {
    const checkSavedGame = async () => {
      try {
        const info = await gameStateManager.getSavedGameInfo();
        if (info) {
          setSavedGameInfo(info);
          setSavedGameModalOpen(true);
        }
      } catch (error) {
        debugError('Error checking for saved game:', error);
      }
    };
    checkSavedGame();
  }, []);

  // Check if tutorial should be shown on mount (only sets the flag, doesn't open modal)
  useEffect(() => {
    const checkTutorial = () => {
      try {
        const shouldShow = tutorialService.shouldShowTutorial();
        setShouldShowTutorial(shouldShow);
      } catch (error) {
        debugError('Error checking tutorial:', error);
      }
    };
    checkTutorial();
  }, []);

  // Check if changelog should be shown on mount
  // Changelog opens first, then tutorial opens when changelog closes
  useEffect(() => {
    const checkChangelog = () => {
      try {
        const shouldShow = changelogService.shouldShowChangelog(currentVersion);
        setShouldShowChangelog(shouldShow);

        // Show changelog if no saved game modal is open
        if (shouldShow && !savedGameModalOpen) {
          setChangelogModalOpen(true);
        }
      } catch (error) {
        debugError('Error checking changelog:', error);
      }
    };
    checkChangelog();
  }, [currentVersion, savedGameModalOpen]);

  // Prepare interstitial ad on Home mount to give it more time to load
  // This increases the likelihood that an ad will be ready when user starts a game
  useEffect(() => {
    const prepareAd = async () => {
      try {
        await adMobService.initialize();
        const ready = await adMobService.prepareInterstitial();
        debugLog('Interstitial ad prepared from Home:', ready);
      } catch (error) {
        debugLog('AdMob interstitial preparation failed from Home:', error);
      }
    };
    prepareAd();
  }, []);

  // Handle tutorial close (X button) - will show again next time
  const handleTutorialClose = () => {
    setTutorialModalOpen(false);
  };

  // Handle tutorial "Don't show again"
  const handleTutorialDontShowAgain = () => {
    tutorialService.dismissTutorial();
    setTutorialModalOpen(false);
  };

  // Handle resume saved game
  const handleResumeSavedGame = async () => {
    debugLog('═══════════════════════════════════════════════════════');
    debugLog('📂 APP: Iniciando carga de partida guardada');
    debugLog('═══════════════════════════════════════════════════════');

    setSavedGameModalOpen(false);

    // Show changelog after closing saved game modal, then tutorial will show after changelog
    if (shouldShowChangelog) {
      setChangelogModalOpen(true);
    } else if (shouldShowTutorial) {
      setTutorialModalOpen(true);
    }

    if (savedGameInfo) {
      debugLog('📋 savedGameInfo:', savedGameInfo);

      // CAMBIO: Cargar estado completo
      const fullState = await gameStateManager.loadGameState();
      debugLog('💾 fullState cargado desde IndexedDB:', fullState);
      debugLog('💾 fullState.state COMPLETO:', JSON.stringify(fullState.state, null, 2));

      if (fullState && fullState.state) {
        const navigationState = {
          resumeGame: true,
          savedGameState: fullState.state,
          players: fullState.state.players || [],
          gamePoints: fullState.state.gamePoints,
          gameMode: fullState.state.gameMode,
          globalScore: fullState.state.globalScore
        };

        debugLog('🧭 Navegando a:', `/${savedGameInfo.gameType}`);
        debugLog('📦 Con estado de navegación:', navigationState);
        debugLog('   - resumeGame:', navigationState.resumeGame);
        debugLog('   - savedGameState exists:', !!navigationState.savedGameState);
        debugLog('   - players:', navigationState.players);
        debugLog('   - team1Score:', navigationState.savedGameState?.team1Score);
        debugLog('   - team2Score:', navigationState.savedGameState?.team2Score);
        debugLog('═══════════════════════════════════════════════════════');

        // Navegar CON el estado completo, incluyendo players y configuración
        navigate(`/${savedGameInfo.gameType}`, { state: navigationState });
      } else {
        debugError('❌ Failed to load saved game state');
        debugLog('fullState:', fullState);
        // Fallback: ir a setup
        navigate('/setup');
      }
    } else {
      debugError('❌ No savedGameInfo available');
    }
  };

  // Handle decline - start new game
  const handleDeclineSavedGame = async () => {
    await gameStateManager.clearSavedGame();
    setSavedGameModalOpen(false);
    setSavedGameInfo(null);

    // Show changelog after closing saved game modal, then tutorial will show after changelog
    if (shouldShowChangelog) {
      setChangelogModalOpen(true);
    } else if (shouldShowTutorial) {
      setTutorialModalOpen(true);
    }
  };

  // Handle changelog close (X button) - will show again next time
  const handleChangelogClose = () => {
    setChangelogModalOpen(false);

    // Show tutorial after closing changelog if needed
    if (shouldShowTutorial) {
      setTutorialModalOpen(true);
    }
  };

  // Handle changelog "Close and don't show again"
  const handleChangelogCloseAndDontShow = () => {
    changelogService.acknowledgeChangelog(currentVersion);
    setChangelogModalOpen(false);

    // Show tutorial after closing changelog if needed
    if (shouldShowTutorial) {
      setTutorialModalOpen(true);
    }
  };

  return (
    <>
      <Header
        title={t('app.title')}
        showSettings={true}
        onChangelog={() => setChangelogModalOpen(true)}
      />
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1, sm: 2 },
        position: 'relative',
        py: { xs: 1, sm: 2 }
      }}>
        <Container maxWidth={{ xs: 'sm', md: 'lg' }} sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 0.75, sm: 2 },
            width: '100%',
            alignItems: 'stretch',
            pr: { xs: '90px', md: '120px' }
          }}>
            {/* Left column: Description and Instructions */}
            <Box sx={{ flex: 1, display: 'flex' }}>
              <Card elevation={3} sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  py: { xs: 0.5, sm: 2 },
                  px: { xs: 0.75, sm: 2 }
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '1rem' }, lineHeight: { xs: 1.2, sm: 1.5 } }}>
                    {t('app.description')}
                  </Typography>
                </CardContent>
                <Box sx={{ p: { xs: 0.75, sm: 2 }, pt: 0, display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
                  {/* Instrucciones y Tutorial en una sola fila */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    onClick={() => navigate('/instructions')}
                    sx={{ py: { xs: 0.4, sm: 1 }, fontSize: { xs: '0.6rem', sm: '1rem' }, minHeight: { xs: '28px', sm: '42px' } }}
                  >
                    {t('buttons.instructions')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    fullWidth
                    onClick={() => setTutorialModalOpen(true)}
                    sx={{ py: { xs: 0.4, sm: 1 }, fontSize: { xs: '0.6rem', sm: '1rem' }, minHeight: { xs: '28px', sm: '42px' } }}
                  >
                    {t('buttons.view_tutorial')}
                  </Button>
                  {isDebugMode && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="large"
                      fullWidth
                      onClick={handleTestShare}
                      disabled={isTestingShare}
                    >
                      {isTestingShare ? 'Generando...' : '🧪 Test Share'}
                    </Button>
                  )}
                </Box>
              </Card>
            </Box>

            {/* Right column: Sponsors */}
            <Box sx={{ flex: 1, display: 'flex' }}>
              <Card elevation={3} sx={{ width: '100%' }}>
                <CardContent sx={{
                  p: { xs: 0.5, sm: 1.5 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#f5f5f5',
                  '&:last-child': { pb: { xs: 0.5, sm: 1.5 } }
                }}>
                  <Typography variant="subtitle2" color="text.primary" sx={{ mb: { xs: 0, sm: 0.5 }, textAlign: 'center', flexShrink: 0, fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                    {t('sponsors.title')}
                  </Typography>
                  <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', alignItems: 'flex-start', mt: { xs: -0.25, sm: 0 } }}>
                    <ImageCarousel />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Inline Ad for Web - spans full width below boxes */}
          {isWebPlatform && (
            <Box sx={{ width: '100%', mt: { xs: 1.5, sm: 2 }, pr: { xs: '90px', md: '120px' } }}>
              <InlineWebAd
                adClientId={adMobService.getAdSenseConfig()?.clientId}
                adSlot={adMobService.getAdSenseConfig()?.slotId}
              />
            </Box>
          )}
        </Container>

        {/* Floating play button - bottom right */}
        <IconButton
          onClick={() => navigate('/setup')}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            width: { xs: 70, sm: 90 },
            height: { xs: 70, sm: 90 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.5)',
            transition: 'all 0.3s ease',
            zIndex: 1000,
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              boxShadow: '0 12px 35px rgba(102, 126, 234, 0.7)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <PlayArrowIcon sx={{ fontSize: { xs: 40, sm: 48 } }} />
        </IconButton>
      </Box>

      {/* Saved Game Modal */}
      <SavedGameModal
        open={savedGameModalOpen}
        onResume={handleResumeSavedGame}
        onNewGame={handleDeclineSavedGame}
        gameInfo={savedGameInfo}
      />

      {/* Tutorial Modal */}
      <TutorialModal
        open={tutorialModalOpen}
        onClose={handleTutorialClose}
        onCloseAndDontShowAgain={handleTutorialDontShowAgain}
      />

      {/* Changelog Modal */}
      <ChangelogModal
        open={changelogModalOpen}
        version={currentVersion}
        onClose={handleChangelogClose}
        onCloseAndDontShowAgain={handleChangelogCloseAndDontShow}
      />
    </>
  );
}

function App() {
  // Track page views automatically
  usePageTracking();

  useEffect(() => {
    // Initialize sound and vibration service
    soundVibrationService.initialize();
  }, []);

  return (
    <Routes>
      <Route path="/" element={
        <>
          <VersionCheckManager />
          <Home />
        </>
      } />
      <Route path="/setup" element={<GameSetup />} />
      <Route path="/instructions" element={<Instructions />} />
      <Route path="/ronda" element={<Ronda />} />
      <Route path="/baciga" element={<Baciga />} />
    </Routes>
  );
}

export default App;
